import { useEffect, useState } from "react";

import { cn } from "../../lib/utils";

/** What `GET /api/heydex/allowance` answers: the share of the month used, and when it renews. */
interface Allowance {
  readonly used: number;
  readonly resetAt: string;
}

const REFRESH_MS = 15_000;

const isAllowance = (value: unknown): value is Allowance =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as Allowance).used === "number" &&
  typeof (value as Allowance).resetAt === "string";

async function readAllowance(): Promise<Allowance | null> {
  const response = await fetch("/api/heydex/allowance", {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (!response.ok) return null;
  const body: unknown = await response.json();
  return isAllowance(body) ? body : null;
}

/**
 * The reader's monthly HeyDex allowance: how much of it this month's answers have used,
 * and, once it is used up, that HeyDex waits for the first of next month, shown from half
 * used. Read again when the page regains focus and every
 * 15 seconds while it is visible.
 */
export function SidebarAllowance() {
  const [allowance, setAllowance] = useState<Allowance | null>(null);
  useEffect(() => {
    let live = true;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      void readAllowance().then(
        (next) => {
          if (live) setAllowance(next);
        },
        () => {
          if (live) setAllowance(null);
        },
      );
    };
    refresh();
    const timer = window.setInterval(refresh, REFRESH_MS);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      live = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  if (allowance === null || allowance.used < 0.5) return null;
  const spent = allowance.used >= 1;
  const percent = Math.round(Math.min(allowance.used, 1) * 100);
  // The month turns over at midnight UTC; the day shown is that day, wherever the reader is.
  const renews = new Date(allowance.resetAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  return (
    <div
      className="flex flex-col gap-1.5 px-2 pb-1"
      role="meter"
      aria-label="This month's HeyDex allowance"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
    >
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className={cn(spent ? "font-medium text-destructive" : "text-muted-foreground")}>
          {spent ? "Allowance used up" : `${percent}% used this month`}
        </span>
        <span className="shrink-0 text-muted-foreground">Renews {renews}</span>
      </div>
      <div className="h-[3px] overflow-hidden rounded-full bg-foreground/10">
        <div
          className={cn("h-full rounded-full", spent ? "bg-destructive" : "bg-foreground/60")}
          style={{ width: `${Math.max(percent, allowance.used > 0 ? 2 : 0)}%` }}
        />
      </div>
    </div>
  );
}
